import * as React from "react";
import { onValue, query, ref } from "firebase/database";
import { rtdb } from "../../firebase";
import { AnnouncementContext } from "./AnnouncementContext";
import { IAnnouncement } from "../../modules/announcements/types/announcement";
import { useAppSelector } from "../../hooks/useRedux";

interface AnnouncementProviderProps {
    children: React.ReactNode;
}

export default function AnnouncementProvider({
    children,
}: AnnouncementProviderProps) {
    const { practice } = useAppSelector((state) => state.userPractice);

    const [dismissedAt, setDismissedAt] = React.useState<string | null>(null);
    const [appAnnoucements, setAppAnnouncements] = React.useState<
        IAnnouncement[]
    >([]);

    const memoizedValues = React.useMemo(() => {
        const storedClearedAt = practice?.practice_id
            ? localStorage.getItem(`clearedBannerAt_${practice.practice_id}`)
            : null;

        const handleClearedAtValue = () => {
            const clearedAt = new Date(Date.now()).toISOString();

            if (practice?.practice_id) {
                localStorage.setItem(
                    `clearedBannerAt_${practice.practice_id}`,
                    clearedAt
                );
            }

            setDismissedAt(clearedAt);
        };

        return {
            clearedBannerAt: dismissedAt ?? storedClearedAt,
            announcements: appAnnoucements,
            handleClearedAtValue,
        };
    }, [appAnnoucements, dismissedAt, practice?.practice_id]);

    React.useEffect(() => {
        const announcementsRef = query(ref(rtdb, "/announcements"));
        onValue(announcementsRef, (snapShot) => {
            const announcements = snapShot.val();
            if (announcements) {
                const keys = Object.keys(announcements);
                let newAnnouncements: IAnnouncement[] = [];

                keys.forEach((a) => {
                    newAnnouncements = [
                        {
                            ...announcements[a],
                            id: a,
                        },
                        ...newAnnouncements,
                    ];
                });

                setAppAnnouncements(newAnnouncements);
            }
        });
    }, []);

    return (
        <AnnouncementContext.Provider value={memoizedValues}>
            {children}
        </AnnouncementContext.Provider>
    );
}
